import { useId } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CalendarDays, CheckCircle2, Sparkles, Zap } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor } from "@/i18n/locale";
import { eventStartDate, formatAllDayDates, isAllDay, type AgendaDates } from "@/lib/agendaDates";

export interface HomeTodayEvent extends AgendaDates {
  id: string;
  title: string;
  start_at: string;
  tag?: string | null;
}

interface HomeTodayPanelProps {
  loading: boolean;
  checkinRecorded: boolean;
  nextEvent: HomeTodayEvent | null;
  onPrepare: (event: HomeTodayEvent) => void;
  agendaStatus?: "loading" | "error" | "signed-out" | "ready";
  onRetryAgenda?: () => void;
  checkinStatus?: "loading" | "error" | "signed-out" | "ready";
  onRetryCheckin?: () => void;
}

const buttonFocus = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card motion-safe:transition-colors";

/** Presentation only: Home owns the existing queries and preparation handler. */
export default function HomeTodayPanel({
  loading,
  checkinRecorded,
  nextEvent,
  onPrepare,
  agendaStatus = loading ? "loading" : "ready",
  onRetryAgenda,
  checkinStatus = loading ? "loading" : "ready",
  onRetryCheckin,
}: HomeTodayPanelProps) {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const titleId = useId();
  const event = agendaStatus === "ready" ? nextEvent : null;
  const recorded = checkinStatus === "ready" && checkinRecorded;
  const allDay = isAllDay(event);
  const eventDate = event ? eventStartDate(event) : null;
  const dateLabel = eventDate && !Number.isNaN(eventDate.getTime())
    ? allDay ? formatAllDayDates(event, localeTagFor(lang)) : new Intl.DateTimeFormat(localeTagFor(lang), {
      weekday: "short", day: "numeric", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(eventDate)
    : null;

  return (
    <section
      aria-labelledby={titleId}
      aria-busy={loading || agendaStatus === "loading" || checkinStatus === "loading"}
      className="relative mb-5 overflow-hidden rounded-3xl border border-primary/20 bg-card p-5"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/[0.07] via-transparent to-transparent" />
      <div className="relative">
        <h2 id={titleId} className="font-display text-xl font-semibold tracking-tight text-foreground">
          {t("ux.home.today")}
        </h2>

        <div className="mt-5">
          <div className="mb-3 flex items-center gap-2 text-primary">
            <CalendarDays size={16} aria-hidden="true" className="shrink-0" />
            <h3 className="font-display text-sm font-medium leading-snug">
              {t("home.nextMoment.title")}
            </h3>
          </div>

          {agendaStatus === "loading" ? (
            <div className="py-1">
              <p role="status" className="text-sm text-muted-foreground">{t("ux.loading")}</p>
              <div aria-hidden="true" className="mt-3 space-y-2 motion-safe:animate-pulse">
                <div className="h-4 w-4/5 rounded bg-foreground/[0.08]" />
                <div className="h-3 w-3/5 rounded bg-foreground/[0.06]" />
              </div>
            </div>
          ) : agendaStatus === "error" ? (
            <div>
              <p role="alert" className="text-sm leading-relaxed text-muted-foreground">{t("ux.agenda.loadError")}</p>
              <button type="button" onClick={onRetryAgenda} className={`mt-2 min-h-11 rounded-xl px-3 py-2 text-sm font-medium text-primary underline underline-offset-4 ${buttonFocus}`}>
                {t("common.retry")}
              </button>
            </div>
          ) : agendaStatus === "signed-out" ? (
            <div>
              <p role="status" className="text-sm leading-relaxed text-muted-foreground">{t("ux.agenda.signInRequired")}</p>
              <button type="button" onClick={() => navigate("/auth?mode=signin")} className={`mt-2 min-h-11 rounded-xl px-3 py-2 text-sm font-medium text-primary ${buttonFocus}`}>
                {t("auth.signIn")}
              </button>
            </div>
          ) : event ? (
            <>
              <p className="min-w-0 break-words [overflow-wrap:anywhere] font-display text-lg font-semibold leading-snug text-foreground">
                {event.title}
              </p>
              {dateLabel && (
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {allDay && <span>{t("ux.agenda.allDay")} · </span>}
                  <time dateTime={allDay ? event.all_day_start : event.start_at}>{dateLabel}</time>
                </p>
              )}
              {event.tag && (
                <span className="mt-3 inline-block max-w-full break-words [overflow-wrap:anywhere] rounded-lg border border-primary/15 bg-primary/[0.06] px-2.5 py-1 text-xs leading-relaxed text-primary">
                  {event.tag}
                </span>
              )}
              <button
                type="button"
                onClick={() => onPrepare(event)}
                className={`mt-4 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold leading-snug text-primary-foreground hover:bg-primary/90 ${buttonFocus}`}
              >
                <Sparkles size={16} aria-hidden="true" className="shrink-0" />
                <span>{t("home.nextMoment.prepareCta")}</span>
              </button>
            </>
          ) : (
            // No next start does not mean an empty calendar (ongoing events may exist).
            <p className="text-sm leading-relaxed text-muted-foreground">{t("ux.home.agendaPrompt")}</p>
          )}

          <button
            type="button"
            onClick={() => navigate("/agenda")}
            className={`mt-2 flex min-h-11 w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium text-foreground hover:bg-primary/[0.06] ${buttonFocus}`}
          >
            <span>{t("ux.home.openAgenda")}</span>
            <ArrowRight size={16} aria-hidden="true" className="shrink-0 text-primary" />
          </button>
        </div>

        <div className="mt-3 border-t border-border/60 pt-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/[0.08] text-primary">
              {recorded ? <CheckCircle2 size={18} aria-hidden="true" /> : <Zap size={18} aria-hidden="true" />}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-sm font-semibold text-foreground">{t("nav.checkin")}</h3>
              {checkinStatus === "loading" && <p role="status" className="mt-1 text-sm text-muted-foreground">{t("ux.loading")}</p>}
              {checkinStatus === "error" && <p role="alert" className="mt-1 text-sm text-muted-foreground">{t("checkin.loadError")}</p>}
              {checkinStatus === "signed-out" && <p className="mt-1 text-sm text-muted-foreground">{t("checkin.signInRequired")}</p>}
              {recorded && (
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{t("ux.home.checkinRecorded")}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            disabled={checkinStatus === "loading"}
            onClick={() => checkinStatus === "error" ? onRetryCheckin?.() : navigate(checkinStatus === "signed-out" ? "/auth?mode=signin" : "/check-in")}
            className={`mt-3 flex min-h-11 w-full items-center justify-center rounded-xl border border-border/70 px-4 py-2.5 text-sm font-medium leading-snug text-foreground hover:border-primary/40 hover:bg-primary/[0.06] ${buttonFocus}`}
          >
            {t(checkinStatus === "loading" ? "ux.loading" : checkinStatus === "error" ? "common.retry" : checkinStatus === "signed-out" ? "auth.signIn" : recorded ? "ux.home.checkinAgain" : "ux.home.checkinAction")}
          </button>
        </div>
      </div>
    </section>
  );
}
